import { ApolloQueryResult } from 'apollo-client';

import {
    mapBannerFilterToApiFormat,
    mapRowsToBanners
} from '../../mappers/banner/banner';
import {
    getValueFromApiFormat,
    mapAdditionalFilterToApiFormat,
    mapDateRangePresetToApiFormat,
    mapSortParamsToString,
    mapTotalStatsToApiFormat
} from '../../mappers/common/common';
import { getDefaultSort } from '../../service/GridHelpers/GridHelpers';
import { ClientInfo } from '../../typings/common';
import {
    BannersQuery,
    BannersSuggestQuery,
    GdStatRequirementsInput
} from '../../typings/graphql-grid';
import {
    defaultClientInfo,
    GridParams,
    GridSourceParams
} from '../../typings/grid';

export const mapSourceParamsToBannersPreviewQueryVariables = (
    params: GridParams,
    clientInfo: ClientInfo
) => {
    const {
        campaignsIds,
        groupsIds,
        statPeriod,
        filter,
        additionalFilter,
        view
    } = params;

    const apiFormatFilter = filter
        ? mapBannerFilterToApiFormat(filter, clientInfo)
        : {};

    if (additionalFilter !== undefined) {
        apiFormatFilter.archived = mapAdditionalFilterToApiFormat(
            additionalFilter
        );
    }

    let statRequirements;
    if (statPeriod) {
        const { statPreset, statFrom, statTo } = statPeriod;
        statRequirements = {
            preset: statPreset && mapDateRangePresetToApiFormat(statPreset),
            from: statFrom,
            to: statTo
        };
    }

    return {
        orderBy: getDefaultSort(view),
        statRequirements,
        filter: {
            ...((filter || additionalFilter) && apiFormatFilter),
            campaignIdIn: campaignsIds,
            adGroupIdIn:
                groupsIds && groupsIds.length !== 0 ? groupsIds : undefined
        }
    };
};

export const mapSourceParamsToBannersAndSuggestQueryVariables = (
    params: GridParams,
    clientInfo: ClientInfo
) => {
    const {
        campaignsIds,
        groupsIds,
        sortParams,
        statPeriod,
        filter,
        additionalFilter,
        view,
        goalIds,
        campaignsCalcTypeFilter
    } = params;

    const apiFormatFilter = filter
        ? mapBannerFilterToApiFormat(
              filter,
              clientInfo,
              campaignsCalcTypeFilter
          )
        : {};

    if (additionalFilter !== undefined) {
        apiFormatFilter.archived = mapAdditionalFilterToApiFormat(
            additionalFilter
        );
    }

    let statRequirements: GdStatRequirementsInput = {
        goalIds
    };
    if (statPeriod) {
        const { statPreset, statFrom, statTo } = statPeriod;
        statRequirements = {
            ...statRequirements,
            preset: statPreset && mapDateRangePresetToApiFormat(statPreset),
            from: statFrom,
            to: statTo
        };
    }

    return {
        orderBy: sortParams
            ? mapSortParamsToString(sortParams)
            : getDefaultSort(view),
        statRequirements,
        filter: {
            ...((filter || additionalFilter) && apiFormatFilter),
            campaignIdIn: campaignsIds,
            adGroupIdIn:
                groupsIds && groupsIds.length !== 0 ? groupsIds : undefined
        }
    };
};

export const mapBannerQueryResultToEntitiesQueryResponse = (
    result: ApolloQueryResult<BannersQuery>,
    params: GridSourceParams
) => {
    const { client, reqId, operator, constants, placements } = result.data;
    const {
        features,
        info: { workCurrency, countryRegionId },
        ads: bannersResult
    } = client;
    const { features: operatorFeatures } = operator;
    const {
        rowset: bannersRows,
        totalCount,
        adIds,
        cacheKey,
        totalStats
    } = bannersResult;
    const { cost, clicks, shows, ctr } = totalStats;

    return {
        data: {
            data: mapRowsToBanners(
                bannersRows,
                client,
                params,
                workCurrency,
                constants.configuration.canvasUiDomain,
                ((placements && placements.placements) || []).reduce(
                    (acc, { caption, id }) => {
                        acc[id] = caption;

                        return acc;
                    },
                    {} as Record<string, string>
                )
            ),
            total: totalCount,
            cacheKey: getValueFromApiFormat(cacheKey),
            allItemsIds: adIds.map(String)
        },
        clientInfo: {
            ...defaultClientInfo,
            ...features,
            ...operatorFeatures,
            workCurrency,
            countryRegionId
        },
        totalStats: mapTotalStatsToApiFormat({
            cost,
            clicks,
            shows,
            ctr
        }),
        hasObjectsOverLimit: bannersResult.hasObjectsOverLimit,
        reqId
    };
};

export const mapBannerSuggestQueryResultToSuggestQueryResponse = (
    result: ApolloQueryResult<BannersSuggestQuery>
) => {
    return {
        data: result.data.client.ads.rowset.reduce(
            (acc: string[], item: { title: string | null }) => {
                if (item.title) {
                    acc.push(item.title);
                }

                return acc;
            },
            []
        ),
        reqId: result.data.reqId
    };
};
